import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "@/lib/zod-lt";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useStore } from "@/store";

const LICENSE_PLATE_FIELD = "license_plate";
const MODEL_FIELD = "model";
const ID_FIELD = "id";

const transportSchema = z.object({
  [LICENSE_PLATE_FIELD]: z.string().min(1, { message: "Valstybinis numeris privalomas." }),
  [MODEL_FIELD]: z.string().min(1, { message: "Transporto priemonės modelis privalomas." }),
  [ID_FIELD]: z.number().optional(),
});

type TransportFormProps = {
  onTransportFormSubmit?: (transport: z.infer<typeof transportSchema>) => void;
  id?: number;
  license_plate?: string;
  model?: string;
};

export function TransportForm({
  onTransportFormSubmit,
  ...loaderData
}: TransportFormProps): React.JSX.Element {
  const { createTransport, updateTransport, isCreateTransportLoading, isUpdateTransportLoading } =
    useStore((s) => s);

  const form = useForm<z.infer<typeof transportSchema>>({
    mode: "all",
    resolver: zodResolver(transportSchema),
    defaultValues: {
      [LICENSE_PLATE_FIELD]: loaderData?.license_plate ?? "",
      [MODEL_FIELD]: loaderData?.model ?? "",
      [ID_FIELD]: loaderData?.id,
    },
  });

  if (Object.keys(form.formState.errors).length > 0) {
    console.debug(form.formState.errors);
  }

  async function onSubmit(formData: z.infer<typeof transportSchema>): Promise<void> {
    if (loaderData?.id) {
      const updatedTransport = await updateTransport(loaderData.id, {
        license_plate: formData[LICENSE_PLATE_FIELD],
        model: formData[MODEL_FIELD],
      });
      onTransportFormSubmit?.({
        license_plate: updatedTransport.license_plate,
        model: updatedTransport.model,
        id: updatedTransport.id,
      });
    } else {
      const newTransport = await createTransport({
        license_plate: formData[LICENSE_PLATE_FIELD],
        model: formData[MODEL_FIELD],
      });
      onTransportFormSubmit?.({
        license_plate: newTransport.license_plate,
        model: newTransport.model,
        id: newTransport.id,
      });
    }
  }

  const buttonIdleLabel = loaderData?.id ? "Atnaujinti" : "Sukurti";
  const buttonLoadingLabel = loaderData?.id ? "Atnaujinama..." : "Kuriama...";
  const isLoading = isCreateTransportLoading || isUpdateTransportLoading;
  const buttonLabel = isLoading ? buttonLoadingLabel : buttonIdleLabel;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <FormField
            control={form.control}
            name={LICENSE_PLATE_FIELD}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Valstybinis numeris*</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormDescription>Transporto priemonės valstybinis numeris</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={MODEL_FIELD}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Modelis*</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormDescription>Transporto priemonės modelis</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.getValues(ID_FIELD) && (
            <FormField
              control={form.control}
              name={ID_FIELD}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input type="hidden" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
          {buttonLabel}
        </Button>
      </form>
    </Form>
  );
}
